/**
 * Renders an ISF shader as a layer: one program per pass, named pass targets (ping-ponged so a
 * pass can read the previous frame of its own buffer), persistent buffers kept across frames.
 */
import type { VisualizationDef } from '../../../shared/viz';
import {
  createRenderTarget,
  deleteRenderTarget,
  uniform,
  unbindUnits,
  type GL,
  type Program,
  type RenderTarget,
} from '../gl/gl';
import {
  UNIT_INPUT,
  UNIT_PALETTE,
  UNIT_SPECTRUM,
  UNIT_WAVEFORM,
  type FrameContext,
} from '../frame';
import type { PaletteBinding, ProgramCache, VizInstance } from '../viz/instances';
import { evalSizeExpr, isfPrelude, parseIsf, type ParsedIsf } from './parse';

interface Buffer {
  read: RenderTarget;
  write: RenderTarget;
  persistent: boolean;
}

export class IsfViz implements VizInstance {
  private readonly parsed: ParsedIsf | null = null;
  private readonly programs: (Program | null)[] = [];
  private error: string | null = null;
  private readonly buffers = new Map<string, Buffer>();
  private frameIndex = 0;
  private readonly date = new Float32Array(4);
  private readonly floatOk: boolean;

  constructor(
    private readonly gl: GL,
    private readonly cache: ProgramCache,
    private readonly def: VisualizationDef,
  ) {
    this.floatOk = !!gl.getExtension('EXT_color_buffer_float');
    try {
      this.parsed = parseIsf(def.source ?? '', def.name);
    } catch (err) {
      this.error = String(err);
    }
    this.poll();
  }

  private poll(): void {
    const p = this.parsed;
    if (!p || this.error) return;
    const fragment = isfPrelude(p) + p.body;
    for (let i = 0; i < p.passes.length; i++) {
      if (this.programs[i]) continue;
      // One program serves every pass (PASSINDEX selects behaviour), as in ISF.
      const e = this.cache.get(`isf:${this.def.id}`, () => fragment);
      if (e.error) {
        this.error = e.error;
        return;
      }
      this.programs[i] = e.program;
    }
  }

  get ready(): boolean {
    this.poll();
    return (
      !!this.parsed &&
      this.programs.length === this.parsed.passes.length &&
      this.programs.every((p) => p)
    );
  }

  get failed(): boolean {
    return this.error !== null;
  }

  get errorMessage(): string | null {
    return this.error;
  }

  private buffer(name: string, w: number, h: number, persistent: boolean, float: boolean): Buffer {
    const old = this.buffers.get(name);
    if (old && old.read.width === w && old.read.height === h) return old;
    const fmt = float && this.floatOk ? 'rgba16f' : 'rgba8';
    const b: Buffer = {
      read: createRenderTarget(this.gl, w, h, fmt),
      write: createRenderTarget(this.gl, w, h, fmt),
      persistent,
    };
    if (old) {
      // The resolution changed (window resize, or the governor adjusting quality): carry a
      // simulation's state over, rescaled, instead of starting it again from nothing.
      if (persistent) this.rescale(old.read, b.read);
      deleteRenderTarget(this.gl, old.read);
      deleteRenderTarget(this.gl, old.write);
    }
    this.buffers.set(name, b);
    return b;
  }

  private rescale(src: RenderTarget, dst: RenderTarget): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, src.fbo);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, dst.fbo);
    gl.blitFramebuffer(
      0,
      0,
      src.width,
      src.height,
      0,
      0,
      dst.width,
      dst.height,
      gl.COLOR_BUFFER_BIT,
      gl.LINEAR,
    );
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
  }

  render(
    fc: FrameContext,
    params: Float32Array,
    palette: PaletteBinding,
    target: RenderTarget,
  ): void {
    const p = this.parsed;
    if (!p || !this.ready) return;
    const gl = this.gl;
    if (this.frameIndex % 60 === 0) {
      const d = new Date();
      this.date.set([
        d.getFullYear(),
        d.getMonth() + 1,
        d.getDate(),
        d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds(),
      ]);
    }
    const vars: Record<string, number> = { WIDTH: target.width, HEIGHT: target.height };
    // Expose float inputs to size expressions ($name).
    let pi = 0;
    for (const input of p.inputs) {
      if (input.type === 'float') vars[input.name] = params[pi]!;
      pi +=
        input.type === 'color'
          ? 3
          : input.type === 'point2D'
            ? 2
            : input.type === 'audio' || input.type === 'audioFFT'
              ? 0
              : 1;
    }

    for (let pass = 0; pass < p.passes.length; pass++) {
      const spec = p.passes[pass]!;
      const prog = this.programs[pass]!;
      let out = target;
      let buf: Buffer | null = null;
      if (spec.target) {
        const w = spec.width
          ? Math.max(1, Math.round(safeEval(spec.width, vars, target.width)))
          : target.width;
        const h = spec.height
          ? Math.max(1, Math.round(safeEval(spec.height, vars, target.height)))
          : target.height;
        buf = this.buffer(spec.target, w, h, spec.persistent, spec.float);
        out = buf.write;
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, out.fbo);
      gl.viewport(0, 0, out.width, out.height);
      gl.useProgram(prog.program);
      this.setUniforms(fc, prog, p, params, palette, out, pass);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      unbindUnits(gl, UNIT_INPUT, UNIT_INPUT + this.buffers.size + 1);
      if (buf) {
        const swap = buf.read;
        buf.read = buf.write;
        buf.write = swap;
      }
    }
    // A last pass that rendered into a named buffer still needs to reach the layer target.
    const last = p.passes[p.passes.length - 1]!;
    if (last.target) this.copy(fc, this.buffers.get(last.target)!.read, target);
    this.frameIndex++;
  }

  private setUniforms(
    fc: FrameContext,
    prog: Program,
    p: ParsedIsf,
    params: Float32Array,
    palette: PaletteBinding,
    out: RenderTarget,
    pass: number,
  ): void {
    const gl = this.gl;
    const u = (n: string) => uniform(gl, prog, n);
    gl.uniform1f(u('TIME'), fc.time);
    gl.uniform1f(u('TIMEDELTA'), fc.dt);
    gl.uniform2f(u('RENDERSIZE'), out.width, out.height);
    gl.uniform1i(u('FRAMEINDEX'), this.frameIndex);
    gl.uniform1i(u('PASSINDEX'), pass);
    gl.uniform4f(u('DATE'), this.date[0]!, this.date[1]!, this.date[2]!, this.date[3]!);
    gl.uniform1f(u('GLOW_BASS'), fc.bass);
    gl.uniform1f(u('GLOW_MID'), fc.mid);
    gl.uniform1f(u('GLOW_TREBLE'), fc.treble);
    gl.uniform1f(u('GLOW_LEVEL'), fc.level);
    gl.uniform1f(u('GLOW_BEAT_PHASE'), fc.beatPhase);
    gl.uniform1f(u('GLOW_BEAT_PULSE'), fc.pulse * fc.beatReact);
    gl.uniform1f(u('GLOW_BAR_PHASE'), fc.barPhase);
    gl.uniform1f(u('GLOW_BPM'), fc.bpm);
    gl.uniform1f(u('GLOW_ONSET'), fc.onset);
    gl.uniform1f(u('GLOW_ENERGY'), fc.energy);
    gl.uniform1f(u('GLOW_PHRASE_PHASE'), fc.phrasePhase);
    gl.uniform1f(u('GLOW_BEAT_IN_BAR'), fc.beatInBar);
    gl.activeTexture(gl.TEXTURE0 + UNIT_SPECTRUM);
    gl.bindTexture(gl.TEXTURE_2D, fc.spectrumTex);
    gl.uniform1i(u('GLOW_SPECTRUM'), UNIT_SPECTRUM);
    gl.uniform1f(u('GLOW_PALETTE_SHIFT'), palette.shift);
    gl.activeTexture(gl.TEXTURE0 + UNIT_PALETTE);
    gl.bindTexture(gl.TEXTURE_2D, palette.tex);
    gl.uniform1i(u('GLOW_PALETTE'), UNIT_PALETTE);

    let pi = 0;
    for (const input of p.inputs) {
      const loc = u(input.name);
      switch (input.type) {
        case 'float':
          gl.uniform1f(loc, params[pi++]!);
          break;
        case 'bool':
        case 'event':
          gl.uniform1i(loc, params[pi++]! >= 0.5 ? 1 : 0);
          break;
        case 'long':
          gl.uniform1i(loc, input.values![Math.round(params[pi++]!)] ?? input.values![0]!);
          break;
        case 'color':
          gl.uniform4f(loc, params[pi]!, params[pi + 1]!, params[pi + 2]!, 1);
          pi += 3;
          break;
        case 'point2D':
          gl.uniform2f(loc, params[pi]!, params[pi + 1]!);
          pi += 2;
          break;
        case 'audio':
          gl.activeTexture(gl.TEXTURE0 + UNIT_WAVEFORM);
          gl.bindTexture(gl.TEXTURE_2D, fc.waveformTex);
          gl.uniform1i(loc, UNIT_WAVEFORM);
          break;
        case 'audioFFT':
          gl.activeTexture(gl.TEXTURE0 + UNIT_SPECTRUM);
          gl.bindTexture(gl.TEXTURE_2D, fc.spectrumTex);
          gl.uniform1i(loc, UNIT_SPECTRUM);
          break;
      }
    }
    let unit = UNIT_INPUT;
    for (const [name, b] of this.buffers) {
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, b.read.texture);
      gl.uniform1i(u(name), unit);
      unit++;
    }
  }

  private copy(fc: FrameContext, src: RenderTarget, dst: RenderTarget): void {
    const gl = this.gl;
    const e = this.cache.get('builtin:isf-copy', () => COPY_FS);
    if (!e.program) return;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fbo);
    gl.viewport(0, 0, dst.width, dst.height);
    gl.bindVertexArray(fc.vao);
    gl.useProgram(e.program.program);
    gl.activeTexture(gl.TEXTURE0 + UNIT_INPUT);
    gl.bindTexture(gl.TEXTURE_2D, src.texture);
    gl.uniform1i(uniform(gl, e.program, 'uTex'), UNIT_INPUT);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    unbindUnits(gl, UNIT_INPUT, UNIT_INPUT);
  }

  dispose(): void {
    for (const b of this.buffers.values()) {
      deleteRenderTarget(this.gl, b.read);
      deleteRenderTarget(this.gl, b.write);
    }
    this.buffers.clear();
  }
}

const COPY_FS = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uTex;
out vec4 fragColor;
void main() { fragColor = vec4(texture(uTex, vUv).rgb, 1.0); }`;

function safeEval(expr: string, vars: Record<string, number>, fallback: number): number {
  try {
    return evalSizeExpr(expr, vars);
  } catch {
    return fallback;
  }
}
